import classNames from 'classnames';
import React from 'react';
import { useGetCardLatestTransaction } from '../../../hooks/apolloHooks/useGetCardLatestTransaction';
import { UnifiedCard } from '../../../integrations/allkort/graphql/interface/Customer';
import { getFriendlyCardTypeName } from '../../../utils/cardMergeUtils';
import { CloseIcon, InfoIconOutlined, PenIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import CardDisabledEditor from '../cards/CardDisabledEditor';
import CardNameEditor from '../cards/CardNameEditor';
import CardOrderReplacement from '../cards/CardOrderReplacement';
import CardPinEditor from '../cards/CardPinEditor';
import FetchingAnimation from '../FetchingAnimation';
import Tooltip from '../Tooltip';

type CardItemRowProps = {
    card: UnifiedCard;
    isCompany?: boolean;
    onCardUpdate?: () => void;
    isOpen?: boolean;
    onToggle?: (cardId: string) => void;
}

const CardItemRow: React.FC<CardItemRowProps> = ({ card, isCompany = false, onCardUpdate, isOpen = false, onToggle }) => {
    // Resolved from data already on the card (type.shortName / cardType) — no extra lookups or API calls needed
    const friendlyTypeName = getFriendlyCardTypeName(card, isCompany);
    const displayName = friendlyTypeName
        ? friendlyTypeName.charAt(0).toUpperCase() + friendlyTypeName.slice(1)
        : card.type.name;

    // Edit mode is controlled by the parent (ListCardsView) so the open row
    // survives refetch-driven unmount/remount cycles
    const editRow = isOpen;
    const toggleEditRow = () => {
        onToggle?.(card.id);
    };

    // Latest transaction for the "senast använd" column — single-card, single-transaction query
    const { latestTransaction, loading: lastUsedLoading } = useGetCardLatestTransaction(
        card.id ? parseInt(card.id, 10) : null
    );
    // Loading state is rendered as <FetchingAnimation /> in the JSX below;
    // this only computes the resolved value
    const lastUsedDisplay = latestTransaction?.at
        ? new Date(latestTransaction.at).toLocaleDateString('sv-SE')
        : '-';
    // Detailed view (edit mode open): include the time of day as well
    const lastUsedDetailed = latestTransaction?.at
        ? new Date(latestTransaction.at).toLocaleString('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        })
        : '-';
    // Bank cards cannot be edited — the pen is rendered grayed out and
    // non-clickable with a tooltip instead of opening the edit row
    const isBankCard = card.cardType === 'bankcard';

    // Type modifier classes for styling (smartcard vs creditcard, company variant)
    const typeClassName =
        isBankCard
            ? 'type-bankcard'
            : card.type.shortName === 'P'
                ? 'type-smartcard'
                : classNames('type-creditcard', { 'is-company': isCompany });

    return (
        <div className={classNames(
            'single-card-row',
            {
                'open': editRow,
            })}
            id={card.id}
            key={card.id}
        >
            {!editRow ? (
                <>
                    <span className={classNames('single-card-row__type', typeClassName)}>
                        {displayName}
                    </span>

                    <span className="single-card-row__number">
                        {card.maskedPan}
                    </span>

                    <span className="single-card-row__name">
                        {card.name}
                    </span>

                    <span className="single-card-row__last-used">
                        {lastUsedLoading ? (
                            <FetchingAnimation height={27} width={120} />
                        ) : (
                            <>
                                {lastUsedDisplay}
                                {isBankCard && (
                                    <Tooltip position='top-end' text="För bankkort finns just nu inte denna funktion.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </span>

                    {isBankCard ? (
                        <Tooltip position='top-end' text="Bankkort går ej att redigera">
                            <button
                                type="button"
                                disabled
                                className="Button no-style-button"
                                aria-label="Bankkort går ej att redigera"
                            >
                                <PenIcon width={18} height={18} />
                            </button>
                        </Tooltip>
                    ) : (
                        <span className="single-card-row__actions" onClick={toggleEditRow}>
                            <PenIcon width={18} height={18} />
                        </span>
                    )}
                </>
            ) : (
                <>
                    <Button
                        type="button"
                        className="button-close white-bg row-open"
                        aria-label="Stäng kortdetaljer"
                        onClick={toggleEditRow}
                    >
                        <CloseIcon />
                    </Button>

                    <div className="left">
                        <span className="cardnumber-and-type">
                            KORTNUMMER
                            <span className={classNames('card-type', typeClassName)}>
                                {displayName}
                            </span>

                        </span>
                        <span className="card-number">
                            {card.maskedPan}
                        </span>
                        <span className="card-last-used">
                            Senast använd:
                            <span className="card-last-used__date">
                                {lastUsedDetailed}
                                {isBankCard && (
                                    <Tooltip position='top' text="För bankkort finns just nu inte denna funktion.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                )}
                            </span>
                        </span>

                        <div className="card-primary-actions">
                            <h4>Namn och pinkod</h4>
                            <div className="handle-card-name">
                                <div>
                                    <CardNameEditor
                                        cardId={card.id}
                                        isCreditCard={card.type.shortName === 'K' ? true : false}
                                        currentName={card.name || ''}
                                        maskedPan={card.maskedPan || '••••'}
                                        isCompany={isCompany}
                                        onUpdateSuccess={onCardUpdate}
                                    />
                                </div>
                            </div>
                            <div className="handle-card-pin">
                                <div>
                                    <CardPinEditor
                                        cardId={card.id}
                                        isCreditCard={card.type.shortName === 'K' ? true : false}
                                        onUpdateSuccess={onCardUpdate}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="right">
                        <div className="card-secondary-actions">
                            <h4>Andra åtgärder</h4>

                            <div className="handle-card-status">
                                <div>
                                    <CardDisabledEditor
                                        cardId={card.id}
                                        cardType={card.type.shortName || 'K'}
                                        maskedPan={card.maskedPan || '••••'}
                                        cardName={card.name || ''}
                                        isCreditCard={card.type.shortName === 'K' ? true : false}
                                        onDisableSuccess={() => toggleEditRow()}
                                        isCompany={isCompany}
                                    />
                                </div>
                            </div>

                            <div className="handle-order-replacement">
                                <CardOrderReplacement
                                    isCreditCard={card.type.shortName === 'K' ? true : false}
                                    isCompany={isCompany}
                                    hasSmartCard={card.type.shortName === 'P' ? true : false}
                                    hasCreditCard={card.type.shortName === 'K' ? true : false}
                                    card={card}
                                    onSuccess={onCardUpdate}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default CardItemRow;